import React, { useState, useEffect, useMemo } from 'react';
import Modal from '../Popup/Modal';
import { generateFilename } from '../../utils/exportUtils';
import './ExportModal.css';

const ExportModal = ({
  show,
  onClose,
  onExport,
  data,
  allColumns,
  visibleColumns,
  viewName,
  entityName,
  enablePdf = true
}) => {
  const [selectedFormat, setSelectedFormat] = useState('xlsx');
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [filename, setFilename] = useState('');

  useEffect(() => {
    if (visibleColumns && visibleColumns.length > 0) {
      setSelectedColumns((prev) => {
        const next = visibleColumns.map(col => col.key);
        if (prev.length === next.length && prev.every((value, index) => value === next[index])) {
          return prev;
        }
        return next;
      });
    }
  }, [visibleColumns]);

  useEffect(() => {
    if (entityName && viewName) {
      const newFilename = generateFilename(entityName, viewName, selectedFormat);
      setFilename((prev) => (prev === newFilename ? prev : newFilename));
    }
  }, [selectedFormat, viewName, entityName]);

  const { visibleColumnsList, additionalColumnsList } = useMemo(() => {
    if (!allColumns || !visibleColumns) {
      return { visibleColumnsList: [], additionalColumnsList: [] };
    }

    const visibleKeys = new Set(visibleColumns.map(col => col.key));
    const visible = allColumns.filter(col => visibleKeys.has(col.key));
    const additional = allColumns.filter(col => !visibleKeys.has(col.key));

    return {
      visibleColumnsList: visible,
      additionalColumnsList: additional
    };
  }, [allColumns, visibleColumns]);

  const handleFormatChange = (format) => {
    setSelectedFormat(format);
  };

  const handleColumnToggle = (columnKey) => {
    setSelectedColumns(prev => {
      if (prev.includes(columnKey)) {
        const next = prev.filter(key => key !== columnKey);
        if (next.length === prev.length) {
          return prev;
        }
        return next;
      }
      return [...prev, columnKey];
    });
  };

  const handleSelectAll = () => {
    if (selectedColumns.length === allColumns.length) {
      if (selectedColumns.length === 0) {
        return;
      }
      setSelectedColumns([]);
    } else {
      const next = allColumns.map(col => col.key);
      setSelectedColumns(prev => {
        if (prev.length === next.length && prev.every((value, index) => value === next[index])) {
          return prev;
        }
        return next;
      });
    }
  };

  const handleExport = () => {
    if (hasValidation) {
      return Promise.resolve();
    }

    const columnsToExport = allColumns.filter(col => selectedColumns.includes(col.key));

    const result = onExport({
      data,
      columns: columnsToExport,
      filename,
      format: selectedFormat
    });

    if (result && typeof result.then === 'function') {
      return result;
    }

    return Promise.resolve();
  };

  const isColumnSelected = (columnKey) => selectedColumns.includes(columnKey);
  const hasValidation = selectedColumns.length === 0;

  return (
    <Modal
      show={show}
      onCancel={onClose}
      onConfirm={handleExport}
      confirmText="Export"
      cancelText="Cancel"
      confirmDisabled={hasValidation}
      title="Export Data"
      size="large"
      content={
        <div className="export-modal-content">
          <div className="export-section">
            <h4 className="export-section-label">Export Format</h4>
            <div className="format-selection-group">
              <div
                className={`format-option ${selectedFormat === 'xlsx' ? 'selected' : ''}`}
                onClick={() => handleFormatChange('xlsx')}
              >
                <input
                  type="radio"
                  name="format"
                  value="xlsx"
                  checked={selectedFormat === 'xlsx'}
                  onChange={() => handleFormatChange('xlsx')}
                />
                <div className="format-details">
                  <div className="format-label">
                    <i className="bi bi-file-earmark-spreadsheet format-icon excel"></i>
                    Excel (.xlsx)
                  </div>
                  <div className="format-description">
                    Rich formatting, formulas, and styling. Best for analysis and presentations.
                  </div>
                </div>
              </div>

              <div
                className={`format-option ${selectedFormat === 'csv' ? 'selected' : ''}`}
                onClick={() => handleFormatChange('csv')}
              >
                <input
                  type="radio"
                  name="format"
                  value="csv"
                  checked={selectedFormat === 'csv'}
                  onChange={() => handleFormatChange('csv')}
                />
                <div className="format-details">
                  <div className="format-label">
                    <i className="bi bi-filetype-csv format-icon csv"></i>
                    CSV (.csv)
                  </div>
                  <div className="format-description">
                    Simple text format. Universal compatibility with all applications.
                  </div>
                </div>
              </div>

              {/* PDF Format Option */}
              {enablePdf && (
                <div 
                  className={`format-option ${selectedFormat === 'pdf' ? 'selected' : ''}`}
                  onClick={() => handleFormatChange('pdf')}
                >
                  <input
                    type="radio"
                    name="exportFormat"
                    value="pdf"
                    checked={selectedFormat === 'pdf'}
                    onChange={() => handleFormatChange('pdf')}
                    className="format-radio"
                  />
                  <div className="format-details">
                    <div className="format-label">
                      <i className="bi bi-file-earmark-pdf format-icon pdf"></i>
                      PDF (.pdf)
                    </div>
                    <div className="format-description">
                      Professional document format with rich formatting and layout
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="export-section">
            <div className="section-header">
              <h4 className="export-section-label">Select Columns</h4>
              <button
                className="select-all-button"
                onClick={handleSelectAll}
                type="button"
              >
                {selectedColumns.length === allColumns.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>


            {hasValidation && (
              <div className="validation-warning">
                <i className="bi bi-exclamation-triangle"></i>
                Please select at least one column to export.
              </div>
            )}

            <div className="column-groups">
              {visibleColumnsList.length > 0 && (
                <div className="column-group">
                  <div className="column-group-header">Columns</div>
                  <div className="column-checkbox-grid">
                    {visibleColumnsList.map(column => (
                      <div key={column.key} className="column-checkbox-item">
                        <input
                          type="checkbox"
                          id={`visible-${column.key}`}
                          checked={isColumnSelected(column.key)}
                          onChange={() => handleColumnToggle(column.key)}
                        />
                        <label htmlFor={`visible-${column.key}`}>
                          {column.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {additionalColumnsList.length > 0 && (
                <div className="column-group">
                  <div className="column-group-header">Additional Columns</div>
                  <div className="column-checkbox-grid">
                    {additionalColumnsList.map(column => (
                      <div key={column.key} className="column-checkbox-item">
                        <input
                          type="checkbox"
                          id={`additional-${column.key}`}
                          checked={isColumnSelected(column.key)}
                          onChange={() => handleColumnToggle(column.key)}
                        />
                        <label htmlFor={`additional-${column.key}`}>
                          {column.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="export-section">
            <h4 className="export-section-label">File Preview</h4>
            <div className="filename-preview">
              <div className="filename-preview-label">Filename</div>
              <div className="filename-display">
                <i className="bi bi-file-earmark filename-icon"></i>
                <div className="filename-text">{filename}</div>
              </div>
            </div>
          </div>
        </div>
      }
    />
  );
};

export default ExportModal;